import { create } from 'zustand';
import { currentUserClient } from 'shared/domain/currentUser/currentUserClient';
import { logError } from 'setup/logger/logError';
import { createSelectors, StoreName, withDevtools } from 'store/config';
import {
  applyAuthSuccess,
  applyModifyUserSuccess,
  createInitialUserState,
} from './model';
import type { UserStore } from './types';

const useUserStoreBase = create<UserStore>()(
  withDevtools(
    (set, get) => ({
      ...createInitialUserState(),

      actions: {
        applyAuthSuccess: (user) =>
          set((state) => applyAuthSuccess(state, user)),

        applyModifyUserSuccess: (payload) =>
          set((state) => applyModifyUserSuccess(state, payload)),

        reset: () => set(createInitialUserState()),

        refresh: async () => {
          get().actions.applyAuthSuccess(await currentUserClient.refresh());
        },

        authenticate: () =>
          get()
            .actions.refresh()
            .catch((error: unknown) => logError('authentication failed', error)),

        updatePersonalData: async (change) => {
          get().actions.applyModifyUserSuccess(await currentUserClient.updatePersonalData(change));
        },

        updateSuperadminLabel: async (superadminLabel) => {
          get().actions.applyModifyUserSuccess(await currentUserClient.updateSuperadminLabel({ superadminLabel }));
        },

        subscribeToCurrentUserChanges: () =>
          currentUserClient.onPermissionsChanged((userData) =>
            get().actions.applyAuthSuccess(userData)
          ),
      },
    }),
    StoreName.User
  )
);

export const useUserStore = createSelectors(useUserStoreBase);

export const getUserStoreState = (): UserStore => useUserStore.getState();
